'use client';

import { useState } from 'react';
import DashboardStats from './DashboardStats';
import ServicesList from './ServicesList';
import RecentTickets from './RecentTickets';
import BillingOverview from './BillingOverview';
import QuickActions from './QuickActions';
import SystemHealth from './SystemHealth';
import Icon from '@/components/ui/AppIcon';

export default function PortalInteractive() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'services' | 'tickets' | 'billing'>('overview');

  useState(() => {
    setIsHydrated(true);
  });

  const tabs = [
    { id: 'overview' as const, label: 'Overview', icon: 'HomeIcon' },
    { id: 'services' as const, label: 'Services', icon: 'ServerIcon' },
    { id: 'tickets' as const, label: 'Support', icon: 'TicketIcon' },
    { id: 'billing' as const, label: 'Billing', icon: 'CreditCardIcon' }
  ];

  const handleTabChange = (tab: typeof activeTab) => {
    if (!isHydrated) return;
    setActiveTab(tab);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Welcome Banner */}
      <div className="bg-gradient-to-r from-brand-primary to-brand-secondary text-white py-8 lg:py-12">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-3xl lg:text-4xl font-headline font-bold mb-2">
                Welcome back, Acme Corporation
              </h1>
              <p className="text-lg text-white/90">
                Your IT infrastructure is running smoothly. Here's your latest overview.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button className="px-6 py-3 bg-white text-brand-primary hover:bg-white/90 rounded-lg font-cta font-semibold text-sm transition-colors duration-200 flex items-center gap-2">
                <Icon name="PhoneIcon" size={18} variant="outline" />
                <span>Emergency Support</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-card border-b border-border sticky top-16 z-40">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-1 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`flex items-center gap-2 px-6 py-4 font-cta font-semibold text-sm whitespace-nowrap transition-all duration-200 border-b-2 ${
                  activeTab === tab.id
                    ? 'text-brand-primary border-brand-primary' :'text-muted-foreground border-transparent hover:text-primary hover:border-border'
                }`}
              >
                <Icon name={tab.icon as any} size={18} variant="outline" />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8 lg:py-12">
        {activeTab === 'overview' && (
          <div className="space-y-8">
            <DashboardStats />
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
              <div className="lg:col-span-2 space-y-6 lg:space-y-8">
                <SystemHealth />
                <RecentTickets />
              </div>
              <div className="space-y-6 lg:space-y-8">
                <QuickActions />
                <div className="bg-card rounded-lg border border-border p-6">
                  <h3 className="text-lg font-headline font-bold text-primary mb-4">Account Manager</h3>
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-16 h-16 bg-brand-primary/10 rounded-full flex items-center justify-center">
                      <span className="text-2xl font-headline font-bold text-brand-primary">JD</span>
                    </div>
                    <div>
                      <h4 className="text-base font-headline font-semibold text-primary">Jennifer Davis</h4>
                      <p className="text-sm text-muted-foreground">Senior Account Manager</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <button className="w-full px-4 py-2.5 bg-brand-primary hover:bg-brand-primary/90 text-white rounded-lg font-cta font-semibold text-sm transition-colors duration-200 flex items-center justify-center gap-2">
                      <Icon name="EnvelopeIcon" size={16} variant="outline" />
                      <span>Send Message</span>
                    </button>
                    <button className="w-full px-4 py-2.5 bg-card hover:bg-muted border border-border text-primary rounded-lg font-cta font-semibold text-sm transition-colors duration-200 flex items-center justify-center gap-2">
                      <Icon name="PhoneIcon" size={16} variant="outline" />
                      <span>Schedule Call</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'services' && (
          <div className="space-y-8">
            <ServicesList />
          </div>
        )}

        {activeTab === 'tickets' && (
          <div className="space-y-8">
            <RecentTickets />
          </div>
        )}

        {activeTab === 'billing' && (
          <div className="space-y-8">
            <BillingOverview />
          </div>
        )}
      </div>
    </div>
  );
}